// src/services/api.js

import axios from 'axios'
import { io } from 'socket.io-client'
import { useAuthStore } from '../store/auth'
import { useToast } from 'vue-toastification'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://pd.gtechnatech.lk/api',//https://pd.gtechnatech.lk/api',
  timeout: 30000, // Increased timeout to 30 seconds
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }

    // Inject companyId + viewAsRole when in Company View Mode so the backend scopes data correctly
    if (authStore.companyViewMode?.id) {
      const companyId = authStore.companyViewMode.id
      if (config.method === 'get') {
        config.params = { ...config.params, companyId, viewAsRole: 'Administrator' }
      }
    }

    console.log('API Request:', config.method?.toUpperCase(), config.url)
    return config
  },
  (error) => {
    console.error('Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url)

    // Standardize microservice response format
    if (response.data && response.data.success === true) {
      // If there's a data property, return it (handles {success, data, pagination})
      if (response.data.data !== undefined) {
        // Attach metadata if it exists (e.g. pagination) to the array/object
        if (response.data.pagination && Array.isArray(response.data.data)) {
          response.data.data.pagination = response.data.pagination
        }
        return response.data.data
      }
    }

    return response.data
  },
  (error) => {
    const toast = useToast()

    console.error('API Error:', {
      message: error.message,
      code: error.code,
      response: error.response?.status,
      url: error.config?.url
    })

    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 401:
          // Unauthorized - clear auth and redirect to login
          const authStore = useAuthStore()
          authStore.logout()
          window.location.href = '/login'
          break

        case 403:
          toast.error('Access denied. You do not have permission to perform this action.')
          break

        case 404:
          toast.error('Resource not found.')
          break

        case 422:
          // Validation errors
          if (data.errors) {
            Object.values(data.errors).forEach(error => {
              toast.error(error[0])
            })
          } else {
            toast.error(data.message || 'Validation failed')
          }
          break

        case 500:
          toast.error('Server error. Please try again later.')
          break

        default:
          toast.error(data.message || 'An error occurred')
      }
    } else if (error.request) {
      // Network error - more detailed error message
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timeout. Please check your connection and try again.')
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your internet connection.')
      } else {
        toast.error('Network error. Please check your connection.')
      }
    } else {
      // Other error
      toast.error('An unexpected error occurred')
    }

    return Promise.reject(error)
  }
)

// API methods
export const apiService = {
  // Generic CRUD operations
  get: (url, params = {}) => api.get(url, { params }),
  post: (url, data = {}) => api.post(url, data),
  put: (url, data = {}) => api.put(url, data),
  patch: (url, data = {}) => api.patch(url, data),
  delete: (url) => api.delete(url),

  // File upload
  upload: (url, formData, onProgress) => {
    return api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress
    })
  },

  // Download file
  download: (url, filename) => {
    return api.get(url, {
      responseType: 'blob'
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    })
  }
}

// Socket.IO connection
let socket = null

export const socketService = {
  connect: () => {
    if (socket) return socket

    const authStore = useAuthStore()
    const token = authStore.token

    socket = io(import.meta.env.VITE_SOCKET_URL || 'https://pd.gtechnatech.lk', {
      autoConnect: true,
      transports: ['websocket', 'polling'],
      auth: { token }
    })

    socket.on('connect', () => {
      console.log('Socket connected:', socket.id)

      // If companyViewMode is active, join that room
      if (authStore.companyViewMode?.id) {
        socket.emit('join', `company:${authStore.companyViewMode.id}`)
      }
    })

    socket.on('disconnect', () => {
      console.log('Socket disconnected')
    })

    socket.on('error', (error) => {
      console.error('Socket error:', error)
    })

    return socket
  },

  disconnect: () => {
    if (socket) {
      socket.disconnect()
      socket = null
    }
  },

  emit: (event, data) => {
    if (socket) {
      socket.emit(event, data)
    }
  },

  on: (event, callback) => {
    if (socket) {
      socket.on(event, callback)
    }
  },

  off: (event) => {
    if (socket) {
      socket.off(event)
    }
  }
}

export default api
